import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, BookOpen } from "lucide-react";
import { toast } from "sonner";
import RowActions from "../components/RowActions";
import { useAuth } from "../contexts/AuthContext";
import { localName } from "../lib/localName";

const EMPTY = { name: "", name_ar: "", code: "", class_ids: [] };

export default function SubjectsPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [schools, setSchools] = useState([]);
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [school, setSchool] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  useEffect(() => {
    api.get("/schools").then(r => {
      const list = r.data || [];
      setSchools(list);
      setSchool(prev => {
        if (prev) return prev;
        if (!isSuper && user?.school_id && list.some(s => s.id === user.school_id)) return user.school_id;
        return list[0]?.id || "";
      });
    });
  }, [user?.school_id, isSuper]);
  const load = () => {
    if (!school) return;
    api.get(`/subjects?school_id=${school}`).then(r => setItems(r.data || []));
    api.get(`/classes?school_id=${school}`).then(r => setClasses(r.data || []));
  };
  useEffect(() => { load(); }, [school]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it, class_ids: it.class_ids || [] }); setOpen(true); };
  const submit = async () => {
    const payload = { ...form, school_id: school };
    if (editing) await api.put(`/subjects/${editing.id}`, payload);
    else await api.post("/subjects", payload);
    toast.success(t("success")); setOpen(false); load();
  };
  const remove = async (it) => { await api.delete(`/subjects/${it.id}`); toast.success(t("success")); load(); };

  const toggleClass = (cid) => {
    setForm((f) => {
      const has = (f.class_ids || []).includes(cid);
      return { ...f, class_ids: has ? f.class_ids.filter(x => x !== cid) : [...(f.class_ids || []), cid] };
    });
  };

  const classNameById = (cid) => classes.find(c => c.id === cid)?.name || cid.slice(0, 6);

  return (
    <div className="space-y-6">
      <div className="flex justify-between flex-wrap gap-3 items-center">
        <h1 className="text-3xl font-semibold">{t("subjects")}</h1>
        <div className="flex gap-3 flex-wrap">
          <Select value={school} onValueChange={setSchool} disabled={!isSuper}>
            <SelectTrigger className="w-56" data-testid="subjects-school-select"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
            <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button onClick={openNew} disabled={!school} data-testid="new-subject-btn"><Plus className="w-4 h-4 me-2" />{t("new_subject")}</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>{editing ? t("edit") : t("new_subject")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("nameAr")}</Label><Input value={form.name_ar} onChange={e => setForm({ ...form, name_ar: e.target.value })} data-testid="subject-name-ar" /></div>
                <div><Label>{t("nameEn")}</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="subject-name-en" /></div>
                <div><Label>Code</Label><Input value={form.code || ""} onChange={e => setForm({ ...form, code: e.target.value })} /></div>
                <div>
                  <Label>الفصول التي تُدرّس فيها المادة (اختياري)</Label>
                  <p className="text-xs text-muted-foreground mb-2">اترك القائمة فارغة لتكون المادة متاحة لجميع الفصول.</p>
                  <div className="border rounded-lg p-2 max-h-40 overflow-auto space-y-1" data-testid="subject-class-picker">
                    {classes.length === 0 && <div className="text-xs text-muted-foreground p-2">لا توجد فصول في هذه المدرسة.</div>}
                    {classes.map(c => {
                      const checked = (form.class_ids || []).includes(c.id);
                      return (
                        <label key={c.id} className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted/40 cursor-pointer">
                          <input type="checkbox" checked={checked} onChange={() => toggleClass(c.id)} data-testid={`subject-class-${c.id}`} />
                          <span className="text-sm">{c.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
                <Button onClick={submit} className="w-full" data-testid="subject-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(s => (
          <Card key={s.id} className="p-4 border shadow-none card-hover smooth" data-testid={`subject-${s.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-accent/10 text-accent grid place-items-center flex-shrink-0"><BookOpen className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{localName(s, lang)}</div>
                <div className="text-xs text-muted-foreground font-mono truncate">{s.code}</div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {(!s.class_ids || s.class_ids.length === 0) && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">جميع الفصول</span>
                  )}
                  {(s.class_ids || []).slice(0, 4).map(cid => (
                    <span key={cid} className="text-[11px] px-2 py-0.5 rounded-full bg-accent/10 text-accent">{classNameById(cid)}</span>
                  ))}
                  {(s.class_ids || []).length > 4 && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted">+{s.class_ids.length - 4}</span>
                  )}
                </div>
              </div>
              <RowActions item={s} onEdit={openEdit} onDelete={remove} testIdPrefix="subject" />
            </div>
          </Card>
        ))}
        {items.length === 0 && <Card className="p-8 col-span-full text-center text-sm text-muted-foreground border-dashed">{t("no_data")}</Card>}
      </div>
    </div>
  );
}
